import { useEffect, useState } from 'react';
import { Err } from '../ui.js';
import { getPaths, harvestPaths, ApiError } from '../api.js';
import type { PathsResult, Route } from '../api.js';

// ─────────────────────────────────────────────────────────────────────────────
// THE ROUTE RIBBON — how this drawing got designed.
//
// V1 was a force-directed network graph, rejected before it was written (see
// paths-core.ts): four nodes of real data, and it answers "what does my network
// look like" when the question is "who gets me to Riley."
//
// V2 was a row per route, each drawn as its own little diagram. Sketched it and
// three things were wrong:
//
//   · "YOU" repeated on every row ate the left third of the canvas to say the
//     same word five times.
//   · Every connector the same weight, so ranking was invisible — you had to
//     read the badges to learn what the picture should have told you.
//   · A one-hop route and a three-hop route drawn to the same width made the
//     GOOD one look sparse and unfinished. The better answer looked worse.
//
// V3, which is this:
//
//   · ONE shared rail on the left. YOU is said once, and every route leaves
//     from the same anchor, which is what makes them comparable at a glance.
//   · CONNECTOR THICKNESS IS THAT HOP'S STRENGTH. This is the whole point of
//     drawing it: a route is only as good as its weakest link, so the weakest
//     link is the thinnest segment and you can see where a route will break
//     without reading a word.
//   · SHORTER ROUTES ARE PHYSICALLY SHORTER. Fewer hops ends further left, so
//     better looks better spatially instead of sparser.
//   · Cost never appears. "Cost 34" is a number from a ranking function, not a
//     fact about Kyle's life. The order carries it.
//
// Not canvas, not a graph library: inline SVG, so it inherits the theme
// variables, prints, scales, and every node is real focusable text rather than
// pixels a screen reader cannot reach.
// ─────────────────────────────────────────────────────────────────────────────

const ROW_H = 62;
const RAIL_X = 92;
const HOP_W = 176;

/** 1–5 → stroke width. Deliberately non-linear: weak should look fragile. */
const weight = (s: number) => [1, 1.5, 2.5, 4, 6][Math.max(0, Math.min(4, s - 1))];

const TONE: Record<string, string> = {
  high: 'var(--ok)', medium: 'var(--primary)', low: 'var(--muted)',
};

function RouteRow({ r, y, onPick, active }: {
  r: Route; y: number; onPick: () => void; active: boolean;
}) {
  const tone = TONE[r.confidence] ?? 'var(--muted)';
  return (
    <g
      className={`ribbon-row ${active ? 'on' : ''}`}
      onClick={onPick}
      tabIndex={0}
      role="button"
      aria-label={
        `${r.confidence} confidence route: you, then ` +
        r.hops.map((h) => `${h.name}, ${h.detail}`).join(', then ') + `. ${r.why}`
      }
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPick(); } }}
    >
      {r.hops.map((h, i) => {
        const x0 = RAIL_X + i * HOP_W;
        const x1 = x0 + HOP_W;
        return (
          <g key={`${r.id}-${i}`}>
            {/* The connector INTO this hop carries that hop's strength. */}
            <line
              x1={x0} y1={y} x2={x1 - 8} y2={y}
              stroke={tone} strokeWidth={weight(h.strength)}
              strokeLinecap="round"
              strokeDasharray={h.strength <= 2 ? '5 5' : undefined}
            />
            <circle cx={x1} cy={y} r={active ? 7 : 5.5}
              fill="var(--surface)" stroke={tone} strokeWidth="2.5" />
            <text x={x1} y={y - 14} textAnchor="middle" className="ribbon-name">
              {h.name}
            </text>
            <text x={x1} y={y + 20} textAnchor="middle" className="ribbon-detail">
              {h.detail.length > 26 ? `${h.detail.slice(0, 26)}…` : h.detail}
            </text>
          </g>
        );
      })}
    </g>
  );
}

export function Paths({ slug }: { slug: string }) {
  const [d, setD] = useState<PathsResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);

  const load = () => getPaths(slug).then((r) => {
    setD(r); setPicked(r.routes[0]?.id ?? null);
  }).catch((e) => setError(e.message));

  useEffect(() => { void load(); }, [slug]);

  if (error && !d) return <Err>{error}</Err>;
  if (!d) return <p className="loading">Loading…</p>;

  const chosen = d.routes.find((r) => r.id === picked) ?? null;
  const widest = Math.max(1, ...d.routes.map((r) => r.hops.length));
  const height = Math.max(90, d.routes.length * ROW_H + 34);

  return (
    <div className="paths">
      <Err>{error}</Err>

      {d.routes.length === 0 && (
        <div className="warnbox">
          <strong>No route in yet.</strong> {d.empty_reason}
        </div>
      )}

      {d.routes.length > 0 && (
        <svg
          className="ribbon"
          viewBox={`0 0 ${RAIL_X + widest * HOP_W + 30} ${height}`}
          role="img"
          aria-label={`${d.routes.length} introduction routes into ${d.company.name}`}
        >
          {/* One anchor, said once. Every route leaves from the same place,
              which is what makes them comparable at a glance. */}
          <line x1={RAIL_X} y1={20} x2={RAIL_X} y2={height - 14}
            stroke="var(--line-strong)" strokeWidth="1.5" />
          <circle cx={RAIL_X} cy={20 + (d.routes.length - 1) * ROW_H / 2}
            r="16" fill="var(--navy)" />
          <text x={RAIL_X} y={20 + (d.routes.length - 1) * ROW_H / 2 + 4}
            textAnchor="middle" className="ribbon-you">YOU</text>

          {d.routes.map((r, i) => (
            <RouteRow key={r.id} r={r} y={20 + i * ROW_H}
              active={r.id === picked} onPick={() => setPicked(r.id)} />
          ))}
        </svg>
      )}

      {chosen && (
        <div className="routecard">
          <div className="routecard-head">
            <span className={`flag ${chosen.confidence === 'high' ? 'flag-ok'
              : chosen.confidence === 'medium' ? 'flag-warn' : 'flag-mute'}`}>
              {chosen.confidence} confidence
            </span>
            <strong>{chosen.hops.map((h) => h.name).join(' → ')}</strong>
          </div>
          <p className="meta">{chosen.why}</p>
          <p className="routeask">{chosen.ask}</p>
        </div>
      )}

      <div className="actions">
        <button disabled={busy} onClick={async () => {
          setBusy(true); setError(null);
          try {
            const r = await harvestPaths();
            setError(null);
            void load();
            if (!r.total) setError(r.note);
          } catch (e) {
            setError(e instanceof ApiError ? e.message : String(e));
          } finally { setBusy(false); }
        }}>{busy ? 'Looking…' : 'Find connections'}</button>
        <span className="cost">
          {d.edges_total} relationship{d.edges_total === 1 ? '' : 's'} known · no credits
        </span>
      </div>
    </div>
  );
}
